/* eslint-disable react-hooks/exhaustive-deps */
import React, { Dispatch, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Headline, Paragraph, Title } from '@cambridgeassessment/cambridge-ui';
import { buildNow, RerunReason, Sample, Roles } from '@cambridgeassessment/lib-ee-commons';
import { Entry } from '@cambridgeassessment/lib-ee-commons/src/domain/db/entries';
import { AddLearnersModal } from './AddLearnersModal/AddLearnersModal';
import { StyledContainer } from '../Content.styles';
import thunks from '../../../store/series/thunks';
import { setSampleSearchParams } from '../../../store/samplesManagement/actions';
import samplesThunks from '../../../store/samplesManagement/thunks';
import { SearchInputs } from './SearchInputs';
import { RootState } from '../../../store';
import { SampleSearchParams } from '../../../store/samplesManagement/types';
import { TableSortConfig } from './helpers';
import { SampleCardWrapper } from './SampleCard/SampleCardWrapper';
import { Authorized } from 'lib-browser';
import { DATE_DISPLAY_FORMAT_FULL } from '../../../constants';
import { LearnerReplacementModal } from './LearnerReplacementModal/LearnerReplacementModal';
import { AlgoRerunModal } from './AlgoRerun/AlgoRerunModal';
import { CentralModalStyled, LoaderWrapper, ModalSubtitleStyled, ModalTitleStyled } from './ManageSampleChanges.styles';
import Loader from '../../../components/Loader/Loader';
import moment from 'moment';
import { useUserName } from '../../../hooks';

const getConsortiumData = (inputsStates: Required<SampleSearchParams>, dispatch: Dispatch<unknown>): void => {
  dispatch(
    samplesThunks.getConsortiumData(
      inputsStates.businessUnit,
      inputsStates.series?.id,
      inputsStates.centreId,
      inputsStates.assessmentCode,
      inputsStates.componentNumber,
    ),
  );
};

const resolveResultsText = (
  hasError: boolean,
  sample: Sample | undefined,
  sampleSearchParams: SampleSearchParams | undefined,
): JSX.Element | false => {
  const hide = !hasError && !sample;
  const noResults = (hasError && !sample) || sample?.wholeSample?.length === 0;
  const text = noResults ? 'No results' : 'Results';

  return (
    !hide && (
      <Title data-qa-id='search-result-title'>{`${text} for: Series - ${sampleSearchParams?.series?.name}, Component number - ${sampleSearchParams?.componentNumber}, Syllabus number - ${sampleSearchParams?.assessmentCode}, Centre number - ${sampleSearchParams?.centreId}`}</Title>
    )
  );
};

export const ManageSampleChanges = (): JSX.Element => {
  const dispatch = useDispatch();
  const userName = useUserName();

  const [isLearnersModalOpened, setIsLearnersModalOpened] = useState(false);
  const [isReplacementModalOpened, setIsReplacementModalOpened] = useState(false);
  const [algoRerunModal, setAlgoRerunModal] = useState(false);
  const [algoRerunExtraModal, setAlgoRerunExtraModal] = useState(false);

  const [learnerOut, setLearnerOut] = useState<Entry>();

  const [sortConfig, setSortConfig] = useState<TableSortConfig>();

  const [rerunReason, setRerunReason] = useState<RerunReason>();

  const {
    samplesManagement: {
      isSampleLoading,
      isSampleBeingRerun,
      sample,
      hasError,
      consortium,
      sampleSearchParams,
      algoReruns,
    },
    series: { seriesData },
    userData: { businessUnit, selectedRole },
  } = useSelector((state: RootState) => state);

  const now = buildNow()();
  const activeSeries = seriesData?.filter((it) => now.isBetween(it.startDate, it.endDate));

  const [inputsStates, setInputsStates] = useState<SampleSearchParams>({
    ...sampleSearchParams,
    businessUnit: businessUnit?.id,
  });

  const onReplaceClick = (learner: Entry) => {
    setLearnerOut(learner);
    setIsReplacementModalOpened(true);
  };

  const onRerunSelectionSubmit = () => {
    setAlgoRerunExtraModal(true);
    setAlgoRerunModal(false);
  };

  const onRerunResubmit = () => {
    if (rerunReason && sample?._id && businessUnit?.id) {
      dispatch(
        samplesThunks.rerunSampleAlgo(
          sample?._id,
          {
            type: rerunReason.type,
            otherReason: rerunReason.otherReason,
            dateOfRerun: moment().format(`${DATE_DISPLAY_FORMAT_FULL}`).toString(),
            user: userName,
          },
          businessUnit.id,
        ),
      );
    }
    setAlgoRerunExtraModal(false);
    setRerunReason(undefined);
  };

  const onDownloadClick = () => {
    if (businessUnit && inputsStates.series) {
      dispatch(samplesThunks.downloadSampleReport(businessUnit.id, inputsStates.series.id));
    }
  };

  useEffect(() => {
    if (businessUnit && !sampleSearchParams?.series) {
      dispatch(thunks.getSeriesData(businessUnit.id));
    }
  }, [businessUnit, dispatch]);

  useEffect(() => {
    const firstOrNone = activeSeries?.length === 1 ? activeSeries?.[0] : undefined;
    const defaultSeriesValue = sampleSearchParams?.series ?? firstOrNone;
    setInputsStates({ ...inputsStates, series: defaultSeriesValue });
    if (businessUnit && defaultSeriesValue) {
      dispatch(thunks.getSeriesComponentDates(businessUnit.id, defaultSeriesValue.id));
    }
  }, [seriesData]);

  useEffect(() => {
    if (isSampleBeingRerun === false) {
      dispatch(samplesThunks.getSampleAlgoHistory(inputsStates));
    }
  }, [isSampleBeingRerun]);

  const onSearchClick = () => {
    dispatch(samplesThunks.findSample(inputsStates));
    dispatch(setSampleSearchParams(inputsStates));
    getConsortiumData(inputsStates as Required<SampleSearchParams>, dispatch);
    dispatch(samplesThunks.getSampleAlgoHistory(inputsStates));
    setSortConfig(undefined);
  };

  return (
    <Authorized roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]} selectedRole={selectedRole}>
      <StyledContainer data-qa-id='page-priming-manage-sample-changes'>
        <Headline data-qa-id='manage-sample-headline'>Manage moderated sample selections</Headline>
        <Paragraph data-qa-id='manage-sample-explanatory-page-text'>
          View and manage moderated sample selection. Request additional work, substitute candidates in a sample
          selection, or re-run the sample selection.
        </Paragraph>
        <SearchInputs
          businessUnitId={businessUnit?.id ?? ''}
          seriesData={activeSeries}
          inputsStates={inputsStates}
          setInputsStates={setInputsStates}
          onSearchClick={onSearchClick}
          onDownloadClick={onDownloadClick}
        />

        {resolveResultsText(hasError, sample, sampleSearchParams)}

        {isSampleBeingRerun ? (
          <LoaderWrapper>
            <Loader />
            <div>Sampling selection is being re-run. Please wait.</div>
          </LoaderWrapper>
        ) : (
          <SampleCardWrapper
            consortium={consortium}
            setSortConfig={setSortConfig}
            setIsModalOpened={setIsLearnersModalOpened}
            sortConfig={sortConfig}
            isSampleLoading={isSampleLoading}
            sample={sample}
            sampleSearchParams={sampleSearchParams}
            onReplaceClick={onReplaceClick}
            onRerunClick={() => setAlgoRerunModal(true)}
            algoReruns={algoReruns}
          />
        )}

        {isLearnersModalOpened && (
          <AddLearnersModal
            sampleId={sample?._id ?? ''}
            onCloseClick={() => setIsLearnersModalOpened(false)}
            refetchList={() => {
              dispatch(samplesThunks.findSample(inputsStates));
            }}
          />
        )}

        {isReplacementModalOpened && (
          <LearnerReplacementModal
            sampleId={sample?._id ?? ''}
            onCloseClick={() => setIsReplacementModalOpened(false)}
            refetchList={() => {
              dispatch(samplesThunks.findSample(inputsStates));
            }}
            learnerOut={learnerOut}
          />
        )}

        {algoRerunModal && (
          <AlgoRerunModal
            rerunReason={rerunReason}
            setRerunReason={setRerunReason}
            onCloseClick={() => setAlgoRerunModal(false)}
            onSubmit={onRerunSelectionSubmit}
          />
        )}

        <CentralModalStyled
          open={algoRerunExtraModal}
          dataQaId='algo-rerun-modal-extra'
          btnRightText='Yes, re-run'
          btnLeftText='Cancel'
          onClickBtnRight={onRerunResubmit}
          onClickBtnLeft={() => setAlgoRerunExtraModal(false)}
        >
          <ModalTitleStyled>Re-run sample selection</ModalTitleStyled>
          <ModalSubtitleStyled noPadding>Are you sure want to re-run the sampling selection?</ModalSubtitleStyled>
          <ModalSubtitleStyled>This action is irreversible.</ModalSubtitleStyled>
        </CentralModalStyled>
      </StyledContainer>
    </Authorized>
  );
};
